import React from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { CheckCircle, ArrowRight, Home, FileSearch, Clock, Mail, Inbox } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Success = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  return (
    <>
      <Helmet>
        <title>Payment Successful - ActionFraudOne</title>
        <meta name="description" content="Your payment was successful. You can now submit your scam check request." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-black flex items-center justify-center py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <Card className="bg-dark-black border border-gold/30 overflow-hidden">
              <div className="bg-gradient-to-r from-gold/20 via-gold/10 to-gold/20 p-1" />
              
              <CardContent className="p-8 md:p-12 text-center">
                {/* Success Icon */}
                <div className="w-20 h-20 mx-auto mb-6 bg-green-500/20 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>

                {/* Main Message */}
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  Payment Successful
                </h1>
                
                <p className="text-lg text-gray-400 mb-8">
                  Thank you for your purchase. Your Scam Check has been activated successfully.
                </p>

                {/* Reference ID */}
                {sessionId && (
                  <div className="bg-black/50 rounded-lg p-4 mb-8 text-left">
                    <p className="text-sm text-gray-500 mb-1">Reference ID:</p>
                    <p className="text-gold font-mono text-sm break-all">{sessionId}</p>
                  </div>
                )}

                {/* What Happens Next */}
                <div className="bg-gold/5 border border-gold/20 rounded-lg p-6 mb-8 text-left">
                  <h3 className="text-gold font-semibold mb-4">What happens next?</h3>
                  <ul className="space-y-3 text-gray-300 text-sm">
                    <li className="flex items-start gap-3">
                      <FileSearch className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <span>You can now submit your suspicious message, website, phone number or offer for review</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <span>Our investigators will analyse your submission</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <Mail className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                      <span>You will receive your Scam Risk Report within 24–48 hours depending on the selected service level</span>
                    </li>
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/">
                    <Button variant="outline" className="border-gold/50 text-gold hover:bg-gold/10 px-6 py-5">
                      <Home className="w-5 h-5 mr-2" />
                      Back to Home
                    </Button>
                  </Link>
                  <Link to={sessionId ? `/scam-checker?session_id=${sessionId}` : '/scam-check'}>
                    <Button className="bg-gold hover:bg-gold/90 text-black font-semibold px-6 py-5">
                      Start Scam Check
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                  </Link>
                </div>

                {/* Support Note */}
                <p className="text-gray-500 text-sm mt-8">
                  Questions? Contact us at{' '}
                  <a href="mailto:info@actionfraudone.com" className="text-gold hover:underline">
                    info@actionfraudone.com
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
};

export default Success;
